import React, { useEffect, useState, useRef } from "react";
import { Col, Row, FormGroup, Label, Input, Card, CardHeader, CardBody, Button, Modal, ModalBody, ModalHeader, Label as ModalLabel } from "reactstrap";
import { useFetch2 } from "components/utils/authenticatedHook";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import LottieLoader from "../LottieLoader";
import lottieEmptyFile from "../../files/animation/empty_new.json";
import lottieLoaderFile from "../../files/animation/loading.json";
import moment from "moment-timezone";
import { pasteValue } from "../Reusable/OnPasteValue";
import userImage from "../../files/user.png";
import callingIcon from "../../files/called.png";
import ViewRPMCarePlan from "../Reusable/RPMCarePlan/ViewRPMCarePlan";
import ReactToPrint from "react-to-print";
import { useDispatch, useSelector } from "react-redux";

const _ = require("lodash");

function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function priceFormatter(column, colIndex, { sortElement, filterElement }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <span className="mb-2">{filterElement}</span>
      {column.text}
      {sortElement}
    </div>
  );
}

// ─────────────────────────────────────────────────────
// Inner expanded row: all care plans for one patient
// ─────────────────────────────────────────────────────
const PatientCarePlanRows = ({ row, onView }) => {
  const plans = row.rpmCarePlanData || [];

  const innerColumns = [
    {
      dataField: "medicalDirector",
      text: "Medical Director / Supervisor",
      formatter: () => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img
            src={userImage}
            alt="avatar"
            style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover" }}
          />
          <span>Dr. Daniel Beirne</span> {/* keep in sync with MEDICAL_DIRECTOR_NAME in RPMCarePlanForm.js */}
        </div>
      ),
      headerStyle: () => ({ width: "200px", textAlign: "left", padding: "12px 16px", fontSize: "14px", color: "#070707" }),
    },
    {
      dataField: "createdBy",
      text: "Created By",
      formatter: () =>
        row.mainCareProviders
          ? <span>{row.mainCareProviders}</span>
          : <div className="text-center">-</div>,
      headerStyle: () => ({ width: "160px", textAlign: "left", padding: "12px 16px", fontSize: "14px", color: "#070707" }),
    },
    {
      dataField: "carePlanCreatedDate",
      text: "Created Date",
      formatter: (cell, r) => {
        const d = r.carePlanCreatedDate || r.createdAt;
        return d ? moment(d).tz(moment.tz.guess()).format("MMMM DD, YYYY hh:mm:ss A") : <div className="text-center">-</div>;
      },
      headerStyle: () => ({ width: "200px", textAlign: "left", padding: "12px 16px", fontSize: "14px", color: "#070707" }),
    },
    {
      dataField: "lastModifyDate",
      text: "Last Modified Date",
      formatter: (cell, r) => {
        const d = r.lastModifyDate || r.updatedAt;
        return d ? moment(d).tz(moment.tz.guess()).format("MMMM DD, YYYY hh:mm:ss A") : <div className="text-center">-</div>;
      },
      headerStyle: () => ({ width: "200px", textAlign: "left", padding: "12px 16px", fontSize: "14px", color: "#070707" }),
    },
    {
      dataField: "_id",
      text: "Action",
      headerStyle: () => ({ width: "160px", textAlign: "left", padding: "12px 16px", fontSize: "14px", color: "#070707" }),
      formatter: (cell, r) => (
        <div className="div-center" style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            className={`btn btn-info ml-2 add-ccm-form`}
            onClick={() => {
              onView(r)
            }}>
            <i className="mdi mdi-eye"></i>&nbsp;View
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Row style={{ margin: "15px 0px" }}>
        <Col sm="12" className="mt-2 remove-summary-paddding">
          <Card style={{ border: "0px 10px 10px 0px" }}>
            <CardHeader className="text-center summary-title summary-header">
              {capitalize(row.fname)} {capitalize(row.lname)} - RPM Care Plan Details
            </CardHeader>
            <CardBody className="padding-card-body">
              <div className="scrollbar-table-cls">
                  <BootstrapTable
                    wrapperClasses="table-responsive"
                    keyField="_id"
                    bordered={true}
                    hover={plans.length > 0}
                    condensed
                    data={plans}
                    columns={innerColumns}
                    noDataIndication={() => (
                      <LottieLoader
                        loadLoop={false}
                        autoplayLoop={true}
                        source={lottieEmptyFile}
                        Lheight={150}
                        Lwidth={150}
                        title={"Sorry! No RPM Care Plan found :("}
                        subTitle={"We can't find any RPM care plan record."}
                      />
                    )}
                  />
                </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// ─────────────────────────────────────────────────────
// Main component: patient accordion outer table
// ─────────────────────────────────────────────────────
function RpmCarePlanList(props) {
  const dispatch = useDispatch();
  const viewRPMRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const [{ response, isLoading }, doFetch] = useFetch2();
  const [totalCount, setTotalCount] = useState(0);
  const [displayData, setDisplayData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [startDateError, setStartDateError] = useState(null);
  const [endDateError, setEndDateError] = useState(null);

  // State for view modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCarePlan, setSelectedCarePlan] = useState(null);

  // Handler for view button click
  const handleView = (carePlan) => {
    setSelectedCarePlan(carePlan);
    setShowViewModal(true);
  };

  // Toggle view modal
  const toggleViewModal = () => {
    setShowViewModal(!showViewModal);
    if (showViewModal) {
      setSelectedCarePlan(null);
    }
  };

  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 10,
    startDate: "",
    endDate: "",
    enrolledPrograms: "",
    search: "",
    sortField: "",
    sortOrder: "",
  });

  useEffect(() => {
    doFetch({
      url: "/allPatients",
      method: "GET",
      disableWait: true,
      query: { selectedApi: "RpmCarePlan", ...queryParams },
    });
  }, [queryParams]);

  useEffect(() => {
    if (response && response.data !== undefined) {
      // Sort active patients first, inactive patients last
      const sorted = (response.data || []).slice().sort((a, b) => {
        if (a.isActive === b.isActive) return 0;
        return a.isActive ? -1 : 1;
      });
      setDisplayData(sorted);
      setTotalCount(response.totalRecode || 0);
    }
  }, [response]);

  function updateFilter(changes) {
    setQueryParams((prev) => ({ ...prev, page: 1, ...changes }));
  }

  function handleStartDateChange(e) {
    const value = e.target.value;
    setStartDateError(null);
    if (queryParams.endDate && value > queryParams.endDate) {
      setStartDateError("Start date cannot be after end date.");
      return;
    }
    updateFilter({ startDate: value });
  }

  function handleEndDateChange(e) {
    const value = e.target.value;
    setEndDateError(null);
    if (queryParams.startDate && value < queryParams.startDate) {
      setEndDateError("End date cannot be before start date.");
      return;
    }
    updateFilter({ endDate: value });
  }

  function handleEnrolledProgramsChange(e) {
    updateFilter({ enrolledPrograms: e.target.value });
  }

  function handleSearchChange(e) {
    const value = e.target.value;
    setSearchValue(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      updateFilter({ search: value });
    }, 500);
  }

  function clearFilters() {
    setStartDateError(null);
    setEndDateError(null);
    setSearchValue("");
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setQueryParams({
      page: 1,
      limit: queryParams.limit,
      startDate: "",
      endDate: "",
      enrolledPrograms: "",
      search: "",
      sortField: "",
      sortOrder: "",
    });
  }

  function handleTableChange(type, { page, sizePerPage, sortField: sf, sortOrder: so }) {
    if (type === "sort") {
      setQueryParams((prev) => ({ ...prev, page: 1, sortField: sf || "", sortOrder: so || "" }));
    } else {
      setQueryParams((prev) => ({ ...prev, page, limit: sizePerPage }));
    }
  }

  const hasActiveFilter =
    queryParams.startDate ||
    queryParams.endDate ||
    queryParams.enrolledPrograms ||
    searchValue;

  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      {"   "}Showing {from} to {to} of {size} Results
    </span>
  );

  const paginationOptions = {
    page: queryParams.page,
    sizePerPage: queryParams.limit,
    totalSize: totalCount,
    firstPageText: "First",
    prePageText: "Back",
    nextPageText: "Next",
    lastPageText: "Last",
    nextPageTitle: "First page",
    prePageTitle: "Pre page",
    firstPageTitle: "Next page",
    lastPageTitle: "Last page",
    hideSizePerPage: totalCount === 0,
    hidePageListOnlyOnePage: true,
    showTotal: totalCount > 0,
    paginationTotalRenderer: customTotal,
    sizePerPageList: [
      { text: "10", value: 10 },
      { text: "100", value: 100 },
      { text: "200", value: 200 },
    ],
  };

  // Outer patient columns — matches ImprovedPatientList (Win Outcome / Follow Up Call tabs)
  const outerColumns = [
    {
      dataField: "lname",
      text: "Last, First Name",
      sort: true,
      headerFormatter: priceFormatter,
      formatter: (cell, row) => {
        const profile = row.profileImage
          ? process.env.REACT_APP_VIDEO_CALL_REQUEST + "/" + row.profileImage
          : userImage;
        return (
          <div className={row.isActive ? "" : " text-danger"}>
            <span>
              <img
                src={profile}
                className="mr-2"
                alt="image"
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null;
                  currentTarget.src = userImage;
                }}
              />
              {capitalize(row.lname)}{", "}{capitalize(row.fname)}
              {row.ccmTrackings && row.ccmTrackings.length > 0 && <img src={callingIcon} alt="called" />}
            </span>
          </div>
        );
      },
      csvFormatter: (cell, row) =>
        row.lname + ", " + row.fname + ` (${row.isActive ? "ACTIVE" : "INACTIVE"})`,
    },
    {
      dataField: "bday",
      text: "DOB",
      sort: true,
      formatter: (cell, row) =>
        row.bday ? moment(row.bday).format("MMMM DD, YYYY") : <div className="text-center">-</div>,
      csvFormatter: (cell, row) =>
        row.bday ? moment(row.bday).format("MMMM DD, YYYY") : "-",
    },
    {
      dataField: "createdAt",
      text: "Enrolled Date",
      sort: true,
      headerFormatter: priceFormatter,
      formatter: (cell, row) =>
        row.createdAt
          ? moment(row.createdAt).tz(moment.tz.guess()).format("MMMM DD, YYYY")
          : <div className="text-center">-</div>,
      csvFormatter: (cell, row) =>
        row.createdAt ? moment(row.createdAt).tz(moment.tz.guess()).format("MMMM DD, YYYY") : "-",
    },
    {
      dataField: "phone",
      text: "Phone Number",
      sort: true,
      headerFormatter: priceFormatter,
      formatter: (cell, row) =>
        row.phone ? row.phone : <div className="text-center">-</div>,
      csvFormatter: (cell, row) => (row.phone ? row.phone : "-"),
    },
    {
      dataField: "deviceReadingDate",
      text: "Last Updated Date",
      sort: false,
      formatter: (cell, row) =>
        row.deviceReadingDate
          ? moment(row.deviceReadingDate).tz(moment.tz.guess()).format("MMMM DD, YYYY")
          : "NONE",
      csvFormatter: (cell, row) =>
        row.deviceReadingDate
          ? moment(row.deviceReadingDate).format("MMMM DD, YYYY")
          : "NONE",
    },
    {
      dataField: "organization",
      text: "Organization",
      sort: true,
      headerFormatter: priceFormatter,
      formatter: (cell, row) =>
        row.organization ? row.organization : <div className="text-center">-</div>,
    },
    {
      dataField: "mainCareProviders",
      text: "Main Care Provider",
      sort: true,
      headerFormatter: priceFormatter,
      formatter: (cell, row) =>
        row.mainCareProviders ? row.mainCareProviders : <div className="text-center">-</div>,
    },
    {
      dataField: "enrolledPrograms",
      text: "Enrolled Program(s)",
      sort: true,
      formatter: (cell, row) =>
        row.enrolledPrograms
          ? <div className="text-center">{row.enrolledPrograms}</div>
          : <div className="text-center">-</div>,
    },
    {
      dataField: "expectationsToFollow",
      text: "Client Expectations to Follow",
      sort: true,
      headerFormatter: priceFormatter,
      formatter: (cell, row) =>
        row.expectationsToFollow ? row.expectationsToFollow : "-",
      csvFormatter: (cell, row) =>
        row.expectationsToFollow ? row.expectationsToFollow : "-",
    },
    {
      dataField: "totalCCMTrackingsTime",
      text: "Total CCM Time",
      sort: true,
      formatter: (cell, row) =>
        row.ccmTotalTimes ? row.ccmTotalTimes : "",
      csvFormatter: (cell, row) =>
        row.ccmTotalTimes ? row.ccmTotalTimes : "",
    },
    {
      dataField: "rpmTotalTimes",
      text: "Total RPM Time",
      sort: true,
      formatter: (cell, row) =>
        row.rpmTotalTimes ? row.rpmTotalTimes : "",
      csvFormatter: (cell, row) =>
        row.rpmTotalTimes ? row.rpmTotalTimes : "",
    },
    {
      dataField: "adhenrenceDay",
      text: "Adherence Days",
      sort: false,
      headerFormatter: priceFormatter,
      formatter: (cell, row) =>
        row.adhenrenceDay ? row.adhenrenceDay : 0,
    },
    {
      dataField: "weekAssigned",
      text: "Designated Week",
      sort: true,
      formatter: (cell, row) =>
        row.weekAssigned
          ? <div className="text-center">{row.weekAssigned}</div>
          : <div className="text-center">-</div>,
      csvFormatter: (cell, row) =>
        row.weekAssigned ? row.weekAssigned : "",
    },
    {
      dataField: "called",
      text: "Called",
      sort: false,
      formatter: (cell, row) => (
        <div className="text-center">
          {row.called
            ? <img src={callingIcon} alt="called" />
            : <div style={{ textAlign: "center" }}>-</div>}
        </div>
      ),
      csvFormatter: (cell, row) => (row.called ? true : false),
    },
  ];

  const expandRow = {
    renderer: (row) => <PatientCarePlanRows row={row} onView={handleView} />,
    onlyOneExpanding: false,
    showExpandColumn: true,
    expandByColumnOnly: false,
  };

  const NoDataIndication = () => (
    <div>
      {isLoading && (
        <LottieLoader
          loadLoop={true}
          autoplayLoop={true}
          source={lottieLoaderFile}
          Lheight={150}
          Lwidth={150}
          subTitle={"Please Wait ..."}
        />
      )}
      {!isLoading && (
        <LottieLoader
          loadLoop={false}
          autoplayLoop={true}
          source={lottieEmptyFile}
          Lheight={300}
          Lwidth={300}
          title={"Sorry! No patient found :("}
          subTitle={"We can't find any patient with RPM care plan."}
        />
      )}
    </div>
  );

  return (
    <Row className="col-sm-12 main-cls-modal remove-table-padding">
      <Col sm="12" className="mt-12">
        {/* Header */}
        {/* <div className="care-plan-header-background">
          <Row>
            <Col sm="12" className="center-label remove-table-padding">
              <h3 className="text-center title-device">
                <i className="mdi mdi-clipboard-text" style={{ marginRight: 8 }}></i>
                RPM Care Plan List
              </h3>
            </Col>
          </Row>
        </div> */}

        {/* Filter row */}
        <Row className="col-md-12 mx-auto float-right remove-pading-table" style={{ marginTop: 35 }}>
          <Col md={3}>
            <FormGroup className="remove-bottom-margin form-group-patient">
              <Label for="rpmStartDate">Start Date (Enrolled Date):</Label>
              <Input
                className="input-date-filter remove-bottom-margin"
                type="date"
                name="startDate"
                id="rpmStartDate"
                max={
                  queryParams.endDate
                    ? moment(queryParams.endDate).format("YYYY-MM-DD")
                    : moment().format("YYYY-MM-DD")
                }
                onChange={handleStartDateChange}
                value={queryParams.startDate}
              />
              <Label style={{ color: "red", textAlign: "left", padding: 5 }}>
                {startDateError}
              </Label>
            </FormGroup>
          </Col>

          <Col md={3}>
            <FormGroup className="remove-bottom-margin form-group-patient">
              <Label for="rpmEndDate">End Date (Enrolled Date)</Label>
              <Input
                className="input-date-filter remove-bottom-margin"
                type="date"
                name="endDate"
                id="rpmEndDate"
                max={moment().format("YYYY-MM-DD")}
                onChange={handleEndDateChange}
                value={queryParams.endDate}
              />
              <Label style={{ color: "red", textAlign: "left", padding: 5 }}>
                {endDateError}
              </Label>
            </FormGroup>
          </Col>

          <Col md={3}>
            <FormGroup className="remove-bottom-margin form-group-patient">
              <Label for="rpmEnrolledPrograms">Enrolled Programs</Label>
              <select
                className="filter-Questionnaire"
                id="rpmEnrolledPrograms"
                onChange={handleEnrolledProgramsChange}
                value={queryParams.enrolledPrograms}
              >
                <option value="">Select Enrolled Program</option>
                <option value="RPM">RPM</option>
                <option value="CCM&RPM">CCM&RPM</option>
              </select>
            </FormGroup>
          </Col>

          <Col md={3}>
            <FormGroup className="remove-bottom-margin form-group-patient">
              <Label>&nbsp;&nbsp;</Label>
              {hasActiveFilter && (
                <button
                  style={{ float: "left", marginTop: 20, width: 150, height: 37 }}
                  onClick={clearFilters}
                  className="btn btn-info ml-2 close-click-cls"
                >
                  <i className="mdi mdi-close" style={{ fontSize: 20 }}></i>
                  Clear Filters
                </button>
              )}
            </FormGroup>
          </Col>
        </Row>

        {/* Search row */}
        <Row className="text-center col-md-12 mx-auto float-right remove-pading-table" style={{ clear: "both", marginBottom: 30 }}>
          <Col md={12} style={{ marginTop: 15 }}>
            <input
              type="text"
              className="form-control input-date-filter remove-bottom-margin"
              id="rpmCarePlanSearch"
              style={{ fontSize: "20px" }}
              placeholder="Search First Name, Last Name, Organization, Main Care Provider..."
              value={searchValue}
              onChange={handleSearchChange}
              onPaste={(e) => {
                e.preventDefault();
                const event = pasteValue(e);
                handleSearchChange(event);
              }}
            />
          </Col>
        </Row>

        {/* Table */}
        <div className="scrollbar-table-cls">
          <BootstrapTable
            remote={{
              sort: true,
              pagination: true,
              filter: true,
              search: true,
            }}
            keyField="_id"
            loading={isLoading}
            data={isLoading ? [] : displayData}
            columns={outerColumns}
            pagination={paginationFactory(paginationOptions)}
            onTableChange={handleTableChange}
            noDataIndication={() => <NoDataIndication />}
            hover={totalCount > 0}
            condensed
            bordered
            wrapperClasses="table-responsive"
            expandRow={expandRow}
          />
        </div>
      </Col>

      {/* View RPM Care Plan Modal - View Only Mode (Print and Close buttons only) */}
      <Modal
        isOpen={showViewModal}
        toggle={toggleViewModal}
        size="lg"
        style={{ width: "50%" }}
        backdrop="static"
      >
        <ModalHeader className="close_button" toggle={toggleViewModal}>
          <ModalLabel className="align-items-center justify-content-center text-center">
            View Remote Patient Monitoring Care Plan
          </ModalLabel>
          <div style={{ display: 'flex', float: 'right', gap: '10px' }}>
            <ReactToPrint
              trigger={() => (
                <button
                  type="button"
                  style={{ width: "130px" }}
                  className="btn btn-info ml-2 print-click-cls">
                  <i className="mdi mdi-printer" style={{ fontSize: 20 }}></i> Print
                </button>
              )}
              content={() => viewRPMRef.current ? viewRPMRef.current.getPrintRef() : null}
            />
            <button
              type="button"
              onClick={toggleViewModal}
              className="btn btn-info ml-2 close-click-cls">
              <i className="mdi mdi-close" style={{ fontSize: 20 }}></i> Close
            </button>
          </div>
        </ModalHeader>
        <ModalBody style={{ backgroundColor: "white" }}>
          {selectedCarePlan && (
            <ViewRPMCarePlan
              ref={viewRPMRef}
              userId={selectedCarePlan.patientId}
              rpmCarePlanId={selectedCarePlan._id}
              onCancel={toggleViewModal}
              isEditing={false}
            />
          )}
        </ModalBody>
      </Modal>
    </Row>
  );
}

export default RpmCarePlanList;
